import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { FormGroup, Input, Row, Col, Spinner } from "reactstrap";
import {
  getBankDetails,
  withdrawFund,
  saveBankDetails,
  deleteUserBank,
} from "Store/Reducer/common.slice";
import { getMarginSummary } from "Store/Reducer/margin.slice";
import useAppLanguage from "hooks/useAppLanguage";

/** Prevent mouse-wheel / trackpad from changing number input while focused. */
function suppressNumberWheel(e) {
  e.target.blur();
}

const initialWithdrawValues = {
  bankId: "",
  upiId: "",
  beneficiaryName: "",
  bankName: "",
  ifscCode: "",
  accountNumber: "",
  amount: "",
};

const validationWithdrawSchema = (maxWithdrawable, t) =>
  Yup.object().shape({
    upiId: Yup.string()
      .matches(/^[\w.-]+@[\w]+$/, t("validUpi"))
      .required(t("upiRequired")),
    beneficiaryName: Yup.string().required(t("beneficiaryRequired")),
    bankName: Yup.string().required(t("bankNameRequired")),
    ifscCode: Yup.string()
      .transform((v) => (typeof v === "string" ? v.trim().toUpperCase() : v))
      .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, t("validIfsc"))
      .required(t("ifscRequired")),
    accountNumber: Yup.string().required(t("accountNumberRequired")),
    amount: Yup.number()
      .typeError(t("amountMustBeNumber"))
      .positive(t("amountPositive"))
      .required(t("amountRequired"))
      .test(
        "max-withdrawable",
        maxWithdrawable != null && Number.isFinite(maxWithdrawable)
          ? t("amountExceedsWithdrawable").replace(
              "{amount}",
              Number(maxWithdrawable).toFixed(2)
            )
          : t("amountExceedsLimit"),
        (value) => {
          if (value == null || value === "") return true;
          if (maxWithdrawable == null || !Number.isFinite(maxWithdrawable)) return true;
          return Number(value) <= Number(maxWithdrawable) + 1e-9;
        }
      ),
  });

const validationBankSchema = (t) =>
  Yup.object().shape({
    upiId: Yup.string()
      .matches(/^[\w.-]+@[\w]+$/, t("validUpi"))
      .required(t("upiRequired")),
    beneficiaryName: Yup.string().required(t("beneficiaryRequired")),
    bankName: Yup.string().required(t("bankNameRequired")),
    ifscCode: Yup.string()
      .transform((v) => (typeof v === "string" ? v.trim().toUpperCase() : v))
      .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, t("validIfsc"))
      .required(t("ifscRequired")),
    accountNumber: Yup.string().required(t("accountNumberRequired")),
  });

const formatBankLabel = (bank, t) => {
  const tail =
    bank?.bankAccountNumber != null && String(bank.bankAccountNumber).length > 0
      ? ` · …${String(bank.bankAccountNumber).slice(-4)}`
      : "";
  return `${bank?.bankName || t("bank")}${tail}`;
};

const applySavedBankToForm = (form, banks, selectedId) => {
  const selectedBank = banks.find((b) => String(b.id) === String(selectedId));
  form.setFieldValue("bankId", selectedId);
  if (selectedBank) {
    form.setFieldValue("beneficiaryName", selectedBank.bankHolderName || "");
    form.setFieldValue("bankName", selectedBank.bankName || "");
    form.setFieldValue("ifscCode", (selectedBank.bankIFSC || "").toUpperCase());
    form.setFieldValue("accountNumber", selectedBank.bankAccountNumber || "");
    form.setFieldValue("upiId", selectedBank.bankUPI || "");
    return;
  }
  form.setFieldValue("beneficiaryName", "");
  form.setFieldValue("bankName", "");
  form.setFieldValue("ifscCode", "");
  form.setFieldValue("accountNumber", "");
  form.setFieldValue("upiId", "");
};

function SavedBankPicker({ banks, value, onSelect }) {
  const { t } = useAppLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const label = useMemo(() => {
    if (!value) return t("selectSavedBank");
    const bank = banks.find((b) => String(b.id) === String(value));
    return bank ? formatBankLabel(bank, t) : t("selectSavedBank");
  }, [banks, t, value]);

  return (
    <div className={`withdraw-bank-picker${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        id="withdraw-saved-bank"
        className="withdraw-bank-picker__trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="withdraw-bank-picker__value">{label}</span>
        <span className="withdraw-bank-picker__chevron" aria-hidden />
      </button>
      {open ? (
        <ul className="withdraw-bank-picker__menu" role="listbox" aria-labelledby="withdraw-saved-bank">
          <li role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={!value}
              className={!value ? "is-active" : ""}
              onClick={() => {
                onSelect("");
                setOpen(false);
              }}
            >
              {t("enterManually")}
            </button>
          </li>
          {(banks || []).map((bank) => (
            <li key={bank.id} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={String(value) === String(bank.id)}
                className={String(value) === String(bank.id) ? "is-active" : ""}
                onClick={() => {
                  onSelect(String(bank.id));
                  setOpen(false);
                }}
              >
                {formatBankLabel(bank, t)}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function BankFormFields({ touched, errors }) {
  const { t } = useAppLanguage();
  return (
    <div className="wallet-mobile-card">
      <Row className="g-3">
        <Col xs="12">
          <FormGroup className="wallet-mobile-field">
            <label className="withdraw-panel__label">UPI ID</label>
            <Field
              as={Input}
              name="upiId"
              placeholder="name@bank"
              className="withdraw-panel__input"
              invalid={touched.upiId && !!errors.upiId}
            />
            <span className="text-danger small wallet-mobile-error">
              <ErrorMessage name="upiId" />
            </span>
          </FormGroup>
        </Col>
        <Col xs="12">
          <FormGroup className="wallet-mobile-field">
            <label className="withdraw-panel__label">{t("beneficiaryName")}</label>
            <Field
              as={Input}
              name="beneficiaryName"
              placeholder={t("accountHolderName")}
              className="withdraw-panel__input"
              invalid={touched.beneficiaryName && !!errors.beneficiaryName}
            />
            <span className="text-danger small wallet-mobile-error">
              <ErrorMessage name="beneficiaryName" />
            </span>
          </FormGroup>
        </Col>
        <Col xs="12" sm="6">
          <FormGroup className="wallet-mobile-field">
            <label className="withdraw-panel__label">{t("bankName")}</label>
            <Field
              as={Input}
              name="bankName"
              placeholder={t("bankName")}
              className="withdraw-panel__input"
              invalid={touched.bankName && !!errors.bankName}
            />
            <span className="text-danger small wallet-mobile-error">
              <ErrorMessage name="bankName" />
            </span>
          </FormGroup>
        </Col>
        <Col xs="12" sm="6">
          <FormGroup className="wallet-mobile-field">
            <label className="withdraw-panel__label">IFSC</label>
            <Field
              as={Input}
              name="ifscCode"
              placeholder="ABCD0123456"
              className="withdraw-panel__input"
              invalid={touched.ifscCode && !!errors.ifscCode}
            />
            <span className="text-danger small wallet-mobile-error">
              <ErrorMessage name="ifscCode" />
            </span>
          </FormGroup>
        </Col>
        <Col xs="12">
          <FormGroup className="wallet-mobile-field wallet-mobile-field--last">
            <label className="withdraw-panel__label">{t("accountNumber")}</label>
            <Field
              as={Input}
              name="accountNumber"
              placeholder={t("accountNumber")}
              className="withdraw-panel__input"
              invalid={touched.accountNumber && !!errors.accountNumber}
            />
            <span className="text-danger small wallet-mobile-error">
              <ErrorMessage name="accountNumber" />
            </span>
          </FormGroup>
        </Col>
      </Row>
    </div>
  );
}

export default function WithdrawPanel({ userId, onWithdrawSuccess, darkTheme = false }) {
  const dispatch = useDispatch();
  const { t } = useAppLanguage();
  const withdrawFormRef = useRef(null);
  const bankFormRef = useRef(null);
  const prevWithdrawLoading = useRef(false);
  const prevBankLoading = useRef(false);
  const {
    bankDetails,
    iswithdrawFund,
    withdrawFundLoading,
    isBankUpdated,
    userBankLoading,
  } = useSelector((s) => s.common);
  const marginSummary = useSelector((s) => s.margin?.marginSummary);
  const withdrawable = useMemo(() => {
    const w = Number(marginSummary?.withdrawable ?? marginSummary?.Withdrawable);
    return Number.isFinite(w) ? Math.max(0, w) : null;
  }, [marginSummary]);
  const withdrawSchema = useMemo(
    () => validationWithdrawSchema(withdrawable, t),
    [t, withdrawable]
  );
  const bankSchema = useMemo(() => validationBankSchema(t), [t]);

  const [subTab, setSubTab] = useState("withdraw");
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);

  useEffect(() => {
    if (userId) {
      dispatch(getBankDetails());
      dispatch(getMarginSummary());
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (
      prevWithdrawLoading.current &&
      !withdrawFundLoading &&
      iswithdrawFund
    ) {
      withdrawFormRef.current?.resetForm({ values: initialWithdrawValues });
      onWithdrawSuccess?.();
    }
    prevWithdrawLoading.current = withdrawFundLoading;
  }, [withdrawFundLoading, iswithdrawFund, onWithdrawSuccess]);

  useEffect(() => {
    if (
      prevBankLoading.current &&
      !userBankLoading &&
      isBankUpdated &&
      bankModalOpen
    ) {
      bankFormRef.current?.resetForm();
      setBankModalOpen(false);
      setEditingBank(null);
      setSubTab("banks");
    }
    prevBankLoading.current = userBankLoading;
  }, [userBankLoading, isBankUpdated, bankModalOpen]);

  const initialBankValues = useMemo(
    () => ({
      bankId: editingBank?.id ?? 0,
      upiId: editingBank?.bankUPI || "",
      beneficiaryName: editingBank?.bankHolderName || "",
      bankName: editingBank?.bankName || "",
      ifscCode: editingBank?.bankIFSC || "",
      accountNumber: editingBank?.bankAccountNumber || "",
    }),
    [editingBank]
  );

  const handleDelete = (e, bankId) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    Swal.fire({
      title: t("removeThisBank"),
      text: t("removeBankText"),
      icon: "warning",
      showCancelButton: true,
      focusCancel: true,
      confirmButtonText: t("yesRemove"),
      cancelButtonText: t("cancel"),
      confirmButtonColor: "#1a7a70",
      cancelButtonColor: "#6c757d",
      customClass: {
        container: "swal2-on-top",
        popup: `swal-bank-confirm${darkTheme ? " swal-bank-confirm--dark" : ""}`,
      },
    }).then((result) => {
      if (result.isConfirmed) dispatch(deleteUserBank(bankId));
    });
  };

  const openAddBank = () => {
    setEditingBank(null);
    setBankModalOpen(true);
  };

  const openEditBank = (bank) => {
    setEditingBank(bank);
    setBankModalOpen(true);
  };

  const closeBankPage = () => {
    setBankModalOpen(false);
    setEditingBank(null);
  };

  return (
    <div className="withdraw-panel wallet-mobile-flow" onClick={(e) => e.stopPropagation()}>
      {withdrawFundLoading && !bankModalOpen && (
        <div className="withdraw-panel__loading">
          <Spinner size="sm" color="secondary" />
        </div>
      )}

      {bankModalOpen ? (
        <div className="withdraw-bank-page wallet-mobile-flow">
          <header className="withdraw-bank-page__header">
            <button
              type="button"
              className="withdraw-bank-page__back"
              onClick={closeBankPage}
              aria-label={t("back")}
            >
              ←
            </button>
            <div className="withdraw-bank-page__titles">
              <h3>{editingBank ? t("editBank") : t("addBank")}</h3>
              <p>{t("savePayoutDetails")}</p>
            </div>
          </header>

          <div className="withdraw-bank-page__body wallet-mobile-flow__scroll">
            {userBankLoading && (
              <div className="withdraw-bank-page__loading">
                <Spinner size="sm" color="secondary" />
                <span>{t("saving")}</span>
              </div>
            )}
            <Formik
              innerRef={bankFormRef}
              initialValues={initialBankValues}
              validationSchema={bankSchema}
              enableReinitialize
              onSubmit={(values) => dispatch(saveBankDetails(values))}
            >
              {({ touched, errors }) => (
                <Form className="withdraw-panel__form wallet-mobile-form">
                  <BankFormFields touched={touched} errors={errors} />
                  <div className="wallet-mobile-form__footer">
                    <button
                      type="submit"
                      className="withdraw-panel__submit wallet-mobile-submit"
                      disabled={userBankLoading}
                    >
                      {t("saveBank")}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      ) : (
        <>
      <div className="withdraw-panel__tabs wallet-mobile-segment" role="tablist">
        <button
          type="button"
          className={subTab === "withdraw" ? "active" : ""}
          onClick={() => {
            setSubTab("withdraw");
            withdrawFormRef.current?.resetForm({ values: initialWithdrawValues });
          }}
        >
          {t("requestWithdraw")}
        </button>
        <button
          type="button"
          className={subTab === "banks" ? "active" : ""}
          onClick={() => setSubTab("banks")}
        >
          {t("myBanks")}
        </button>
      </div>

      {subTab === "withdraw" && (
        <div className="withdraw-panel__body wallet-mobile-flow__scroll">
          <p className="withdraw-panel__hint wallet-mobile-hint">
            {t("withdrawHint")}
          </p>
          <Formik
            innerRef={withdrawFormRef}
            initialValues={initialWithdrawValues}
            validationSchema={withdrawSchema}
            enableReinitialize
            onSubmit={(values) => dispatch(withdrawFund(values))}
          >
            {({ touched, errors, setFieldValue, values }) => (
              <Form className="withdraw-panel__form wallet-mobile-form">
                <section className="wallet-mobile-section wallet-mobile-section--hero">
                  <label className="wallet-mobile-label" htmlFor="withdraw-amount">
                    {t("withdrawAmount")}
                  </label>
                  <div className="wallet-mobile-amount-field">
                    <span className="wallet-mobile-amount-field__prefix" aria-hidden>
                      ₹
                    </span>
                    <Field
                      as={Input}
                      id="withdraw-amount"
                      type="number"
                      name="amount"
                      inputMode="decimal"
                      step="any"
                      placeholder="0.00"
                      className="withdraw-panel__input wallet-mobile-amount-field__input"
                      invalid={touched.amount && !!errors.amount}
                      onWheel={suppressNumberWheel}
                      onKeyDown={(e) => {
                        // Block up/down keys that also nudge number inputs.
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
                      }}
                    />
                  </div>
                  <span className="text-danger small wallet-mobile-error">
                    <ErrorMessage name="amount" />
                  </span>
                </section>

                <section className="wallet-mobile-section">
                  <h4 className="wallet-mobile-section__title">{t("payoutAccount")}</h4>
                  <div className="wallet-mobile-card">
                    <FormGroup className="wallet-mobile-field withdraw-panel__select-field">
                      <label className="withdraw-panel__label" htmlFor="withdraw-saved-bank">
                        {t("savedBank")}
                      </label>
                      <SavedBankPicker
                        banks={bankDetails || []}
                        value={values.bankId}
                        onSelect={(selectedId) =>
                          applySavedBankToForm(
                            { setFieldValue },
                            bankDetails || [],
                            selectedId
                          )
                        }
                      />
                      <Field type="hidden" name="bankId" />
                    </FormGroup>

                    <Row className="g-3">
                      <Col xs="12">
                        <FormGroup className="wallet-mobile-field">
                          <label className="withdraw-panel__label">UPI ID</label>
                          <Field
                            as={Input}
                            name="upiId"
                            placeholder="name@bank"
                            className="withdraw-panel__input"
                            invalid={touched.upiId && !!errors.upiId}
                          />
                          <span className="text-danger small wallet-mobile-error">
                            <ErrorMessage name="upiId" />
                          </span>
                        </FormGroup>
                      </Col>
                      <Col xs="12">
                        <FormGroup className="wallet-mobile-field">
                          <label className="withdraw-panel__label">{t("beneficiaryName")}</label>
                          <Field
                            as={Input}
                            name="beneficiaryName"
                            placeholder={t("accountHolderName")}
                            className="withdraw-panel__input"
                            invalid={touched.beneficiaryName && !!errors.beneficiaryName}
                          />
                          <span className="text-danger small wallet-mobile-error">
                            <ErrorMessage name="beneficiaryName" />
                          </span>
                        </FormGroup>
                      </Col>
                      <Col xs="12" sm="6">
                        <FormGroup className="wallet-mobile-field">
                          <label className="withdraw-panel__label">{t("bankName")}</label>
                          <Field
                            as={Input}
                            name="bankName"
                            placeholder={t("bankName")}
                            className="withdraw-panel__input"
                            invalid={touched.bankName && !!errors.bankName}
                          />
                          <span className="text-danger small wallet-mobile-error">
                            <ErrorMessage name="bankName" />
                          </span>
                        </FormGroup>
                      </Col>
                      <Col xs="12" sm="6">
                        <FormGroup className="wallet-mobile-field">
                          <label className="withdraw-panel__label">IFSC</label>
                          <Field
                            as={Input}
                            name="ifscCode"
                            placeholder="ABCD0123456"
                            className="withdraw-panel__input"
                            invalid={touched.ifscCode && !!errors.ifscCode}
                          />
                          <span className="text-danger small wallet-mobile-error">
                            <ErrorMessage name="ifscCode" />
                          </span>
                        </FormGroup>
                      </Col>
                      <Col xs="12">
                        <FormGroup className="wallet-mobile-field wallet-mobile-field--last">
                          <label className="withdraw-panel__label">{t("accountNumber")}</label>
                          <Field
                            as={Input}
                            name="accountNumber"
                            placeholder={t("accountNumber")}
                            className="withdraw-panel__input"
                            invalid={touched.accountNumber && !!errors.accountNumber}
                          />
                          <span className="text-danger small wallet-mobile-error">
                            <ErrorMessage name="accountNumber" />
                          </span>
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </section>

                <div className="wallet-mobile-form__footer">
                  <button
                    type="submit"
                    className="withdraw-panel__submit wallet-mobile-submit"
                    disabled={withdrawFundLoading}
                  >
                    {t("submitWithdrawRequest")}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

      {subTab === "banks" && (
        <div className="withdraw-panel__body withdraw-panel__banks wallet-mobile-flow__scroll">
          {bankDetails?.length ? (
            bankDetails.map((bank) => (
              <div key={bank.id} className="withdraw-panel__bank-card wallet-mobile-bank-card">
                <div className="wallet-mobile-bank-card__head">
                  <span className="wallet-mobile-bank-card__icon" aria-hidden>🏦</span>
                  <strong className="wallet-mobile-bank-card__name">{bank.bankName}</strong>
                </div>
                <div className="withdraw-panel__bank-row">
                  <span className="withdraw-panel__bank-k">IFSC</span>
                  <code>{bank.bankIFSC}</code>
                </div>
                <div className="withdraw-panel__bank-row">
                  <span className="withdraw-panel__bank-k">{t("account")}</span>
                  <code>{bank.bankAccountNumber}</code>
                </div>
                <div className="withdraw-panel__bank-row">
                  <span className="withdraw-panel__bank-k">UPI</span>
                  <code>{bank.bankUPI}</code>
                </div>
                <div className="withdraw-panel__bank-actions">
                  <button type="button" onClick={() => openEditBank(bank)}>
                    {t("edit")}
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={(e) => handleDelete(e, bank.id)}
                  >
                    {t("remove")}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="withdraw-panel__empty">{t("noSavedBanks")}</p>
          )}
          <button type="button" className="withdraw-panel__add-bank wallet-mobile-add-btn" onClick={openAddBank}>
            {t("addBankAccount")}
          </button>
        </div>
      )}
        </>
      )}
    </div>
  );
}
